import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LuBed,
  LuCalendar,
  LuChevronLeft,
  LuChevronRight,
  LuCoffee,
  LuDroplets,
  LuExpand,
  LuLeaf,
  LuSearch,
  LuSparkles,
  LuStar,
  LuUsers,
  LuWind,
  LuWifi
} from "react-icons/lu";
import ProgressBar from "./ProgressBar";
import {
  getPricingPeriod,
  getRoomRatesForDate,
  ROOMS_CATALOG,
  RESORTS,
  getRoomTypesByResort,
  getDisplayNameForRoomType,
  PRICING_PERIODS
} from "./roomCatalog";
const roomsCatalog = ROOMS_CATALOG;
const RESORT_ROUTES = {
  villa: "/hiru-villa",
  om: "/hiru-om",
  mudra: "/hiru-mudra",
  aadya: "/hiru-aadya"
};
const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const parseInputDate = (value) => {
  const parts = value.split("-");
  if (parts.length !== 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return null;
  }
  return parsed;
};
const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};
const ServiceIcon = ({ iconName }) => {
  const iconMap = {
    Bed: LuBed,
    Maximize: LuExpand,
    Droplets: LuDroplets,
    Wind: LuWind,
    Wifi: LuWifi,
    Coffee: LuCoffee,
    Sparkles: LuSparkles
  };
  const IconComponent = iconMap[iconName] ?? LuSparkles;
  return <IconComponent className="h-4 w-4 text-[#1f4f9c]" />;
};
const getBookingHref = (room, checkInDate, checkOutDate, guests) => {
  const params = new URLSearchParams({
    resortId: room.resortId,
    roomType: room.type,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    guests: String(guests),
    image: room.image
  });
  return `/booking?${params.toString()}`;
};
const Search = () => {
  const [searchParams] = useSearchParams();
  const bookingTexts = {
    heroBrand: "Hiru Ayurveda Resort",
    heroTitle: "Verf\xFCgbarkeit f\xFCr Ihren gew\xFCnschten Aufenthalt pr\xFCfen",
    heroDescription: "W\xE4hlen Sie aus vier Ayurveda-Resorts \u2014 Hiru Villa, Hiru Om, Hiru Mudra und Hiru Aadya \u2014 den passenden Aufenthalt.",
    bookYourStay: "Aufenthalt suchen",
    roomType: "Zimmertyp",
    allRooms: "Alle Zimmer",
    suiteRoom: "Suite",
    deluxeRoom: "Deluxe-Zimmer",
    checkIn: "Check-in",
    checkOut: "Check-out",
    minimumStayFromCheckIn: "Die Mindestaufenthaltsdauer betr\xE4gt 7 N\xE4chte oder mehr",
    numberOfGuests: "G\xE4stezahl",
    singleOccupancyOption: "1 Person",
    doubleOccupancyOption: "2 Personen",
    currentSeason: "Aktuelle Saison",
    seasonSummer: "Sommer",
    seasonWinter: "Winter",
    seasonPeriod: "Preise werden je nach Saison berechnet.",
    updateBooking: "Suchkriterien aktualisieren",
    availableRooms: "Verf\xFCgbare Zimmer",
    seasonRatesSuffix: "Preise",
    pleaseSelectValidDates: "Bitte w\xE4hlen Sie g\xFCltige Daten aus",
    minimumStayRequirement: "Die Mindestaufenthaltsdauer betr\xE4gt 7 N\xE4chte.",
    suiteRoomWithSeaViewTitle: "Meerblick-Suite",
    deluxeRoomWithSeaViewTitle: "Meerblick-Deluxe-Zimmer",
    suiteRoomWithSeaViewDescription: "Ein gro\xDFz\xFCgiges Zimmer mit atemberaubendem Meerblick und ruhiger Atmosph\xE4re f\xFCr tiefe Entspannung und Ayurveda-Balance.",
    deluxeRoomWithSeaViewDescription: "Ein komfortables Zimmer mit Meerblick und ruhiger, entspannter Stimmung f\xFCr einen angenehm entspannenden Aufenthalt.",
    nightsLabel: "N\xE4chte",
    allSeasonalRates: "Preise je nach Saison",
    summerRateRange: "Sommerpreise",
    winterRateRange: "Winterpreise",
    singleLabel: "1 Person",
    doubleLabel: "2 Personen",
    totalForNights: "Gesamt f\xFCr {count} N\xE4chte",
    upToGuests: "Bis zu {count} G\xE4ste",
    occupancySeason: "{occupancy} \xB7 {season}",
    perNight: "pro Nacht",
    viewDetails: "Zimmerdetails",
    continueBooking: "Weiter zur Buchung",
    single: "1 Person",
    double: "2 Personen",
    seasonSummerShort: "Sommer",
    seasonWinterShort: "Winter"
  };
  const t = (key) => bookingTexts[key];
  const today = useMemo(() => /* @__PURE__ */ new Date(), []);
  const defaultCheckOut = useMemo(() => addDays(today, 7), [today]);
  const [checkInDate, setCheckInDate] = useState(toDateInputValue(today));
  const [checkOutDate, setCheckOutDate] = useState(toDateInputValue(defaultCheckOut));
  const [selectedResort, setSelectedResort] = useState("villa");
  const [selectedType, setSelectedType] = useState("all");
  const [guests, setGuests] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const roomsPerPage = 6;
  useEffect(() => {
    const queryCheckIn = searchParams.get("checkIn");
    const parsedCheckIn = queryCheckIn ? parseInputDate(queryCheckIn) : null;
    if (parsedCheckIn) {
      setCheckInDate(toDateInputValue(parsedCheckIn));
      const minCheckout = addDays(parsedCheckIn, 7);
      const queryCheckOut = searchParams.get("checkOut");
      const parsedCheckOut = queryCheckOut ? parseInputDate(queryCheckOut) : null;
      setCheckOutDate(toDateInputValue(parsedCheckOut && parsedCheckOut >= minCheckout ? parsedCheckOut : minCheckout));
    }
  }, [searchParams]);
  useEffect(() => {
    setCurrentPage(1);
    setSelectedType("all");
  }, [checkInDate, checkOutDate, selectedResort, guests]);
  const handleCheckInChange = (value) => {
    const nextCheckIn = parseInputDate(value);
    if (!nextCheckIn) return;
    setCheckInDate(value);
    setCheckOutDate(toDateInputValue(addDays(nextCheckIn, 7)));
  };
  const handleCheckOutChange = (value) => {
    const selectedCheckOut = parseInputDate(value);
    const selectedCheckIn = parseInputDate(checkInDate);
    if (!selectedCheckOut || !selectedCheckIn) return;
    const minCheckout = addDays(selectedCheckIn, 7);
    setCheckOutDate(toDateInputValue(selectedCheckOut < minCheckout ? minCheckout : selectedCheckOut));
  };
  const handleSearch = () => {
    setCurrentPage(1);
  };
  const displayedRooms = useMemo(() => {
    const startDate = parseInputDate(checkInDate);
    const endDate = parseInputDate(checkOutDate);
    if (!startDate || !endDate) {
      return [];
    }
    const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1e3 * 60 * 60 * 24));
    if (nights < 7) {
      return [];
    }
    const pricingPeriod = getPricingPeriod(startDate);
    if (!pricingPeriod) {
      return [];
    }
    const season = pricingPeriod.season;
    return roomsCatalog.filter((room) => room.resortId === selectedResort).filter((room) => selectedType === "all" ? true : room.type === selectedType).map((room) => {
      const periodRates = getRoomRatesForDate(room, startDate);
      const price = guests === 1 ? periodRates.single : periodRates.double;
      return {
        ...room,
        season,
        periodLabel: pricingPeriod.label,
        periodRates,
        price,
        totalPrice: price * nights,
        nights
      };
    });
  }, [checkInDate, checkOutDate, guests, selectedType, selectedResort]);
  const totalPages = Math.ceil(displayedRooms.length / roomsPerPage);
  const safeCurrentPage = Math.min(currentPage, Math.max(totalPages, 1));
  const paginatedRooms = displayedRooms.slice((safeCurrentPage - 1) * roomsPerPage, safeCurrentPage * roomsPerPage);
  const isFirstPage = safeCurrentPage === 1;
  const isLastPage = totalPages === 0 || safeCurrentPage === totalPages;
  const checkIn = parseInputDate(checkInDate);
  const activePricingPeriod = checkIn ? getPricingPeriod(checkIn) : void 0;
  const activeSeason = activePricingPeriod?.season ?? "Winter";
  const activeSeasonLabel = activeSeason === "Summer" ? t("seasonSummer") : t("seasonWinter");
  const getSeasonLabel = (season) => season === "Summer" ? t("seasonSummer") : t("seasonWinter");
  const getUpToGuestsLabel = (count) => t("upToGuests").replace("{count}", String(count));
  const getTotalForNightsLabel = (count) => t("totalForNights").replace("{count}", String(count));
  const getOccupancySeasonLabel = (occupancy, season) => t("occupancySeason").replace("{occupancy}", occupancy).replace("{season}", getSeasonLabel(season));
  const getRoomDisplayName = (room) => room.name;
  const getRoomDisplayDescription = (room) => room.description;
  const getCategoryLabel = (room) => getDisplayNameForRoomType(room.type);
  const getGuestUsageLabel = (count) => count === 1 ? "1 Person" : "2 Personen";
  return <>
            {
    /* Hero background image starts at the very top */
  }
            <div className="relative isolate min-h-[300px] overflow-hidden sm:min-h-[380px] md:min-h-[450px] lg:min-h-[560px] xl:min-h-[600px]">
                <div className="absolute inset-0">
                    <img
    src="/images/Hiru/public_images/680A2495-Enhanced-NR-Edit-copy.webp"
    alt="Room booking hero"
    className="object-cover object-center" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} loading="eager" fetchPriority="high" />
                </div>
                <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(12,35,28,0.82)_0%,rgba(18,52,42,0.72)_35%,rgba(18,52,42,0.28)_68%,rgba(18,52,42,0.16)_100%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.16),transparent_30%)]" />

                <div className="relative mx-auto flex h-full w-full max-w-7xl items-center px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl pb-7 pt-36 sm:pb-10 sm:pt-48 md:pt-24 lg:pb-12 lg:pt-48">
                        <div className="mb-3 flex items-center gap-2 sm:mb-4">
                            <LuLeaf className="h-5 w-5 text-[#F4D68A] sm:h-6 sm:w-6 md:h-8 md:w-8" />
                            <span className="text-[10px] font-light tracking-[0.18em] text-amber-100 sm:text-xs md:text-sm">
                                {t("heroBrand")}
                            </span>
                        </div>
                        <h1 className="text-2xl font-light leading-tight text-white sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
                            {t("heroTitle")}
                        </h1>
                        <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-amber-50/95 sm:mt-4 sm:text-base md:text-lg lg:text-xl">
                            {t("heroDescription")}
                        </p>
                    </div>
                </div>
            </div>

            {
    /* Main content with top padding below hero image */
  }
            <div className="min-h-screen bg-white pt-16 sm:pt-20 md:pt-24">
                <ProgressBar currentStep="search" />

                <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 md:py-10 lg:px-8 lg:py-12">
                    <div className="grid gap-6 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] lg:gap-8">
                        <div className="w-full">
                            <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-lg sm:p-5 lg:sticky lg:top-24 lg:p-6">
                                <h2 className="mb-5 flex items-center text-xl font-light text-gray-900 sm:mb-6 sm:text-2xl">
                                    <LuSearch className="mr-2 h-6 w-6 text-[#1f4d3c]" />
                                    {t("bookYourStay")}
                                </h2>

                                <div className="mb-5 sm:mb-6">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">Resort auswählen</label>
                                    <select
    value={selectedResort}
    onChange={(e) => setSelectedResort(e.target.value)}
    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-black [color-scheme:light] focus:border-[#1f4d3c] focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:text-base"
  >
                                        {RESORTS.map((resort) => <option key={resort.id} value={resort.id}>
                                                {resort.japanName}
                                            </option>)}
                                    </select>
                                </div>

                                <div className="mb-5 sm:mb-6">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">{t("roomType")}</label>
                                    <select
    value={selectedType}
    onChange={(e) => setSelectedType(e.target.value)}
    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-black [color-scheme:light] focus:border-[#1f4f9c] focus:outline-none focus:ring-2 focus:ring-blue-200 sm:text-base"
  >
                                        <option value="all">Alle Zimmer</option>
                                        {getRoomTypesByResort(selectedResort).map((roomType) => <option key={roomType} value={roomType}>
                                                {getDisplayNameForRoomType(roomType)}
                                            </option>)}
                                    </select>
                                </div>

                                <div className="mb-5 sm:mb-6">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">{t("checkIn")}</label>
                                    <div className="relative">
                                        <input
    type="date"
    value={checkInDate}
    min={toDateInputValue(today)}
    max={PRICING_PERIODS.reduce((latest, period) => period.end > latest ? period.end : latest, PRICING_PERIODS[0].end)}
    onChange={(e) => handleCheckInChange(e.target.value)}
    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 pr-10 text-sm text-black [color-scheme:light] hover:border-[#1f4d3c] focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:text-base"
  />
                                        <LuCalendar className="pointer-events-none absolute right-3 top-3 h-5 w-5 text-gray-400" />
                                    </div>
                                </div>

                                <div className="mb-5 sm:mb-6">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">{t("checkOut")}</label>
                                    <div className="relative">
                                        <input
    type="date"
    value={checkOutDate}
    min={(() => {
      const minDate = addDays(parseInputDate(checkInDate) ?? today, 7);
      return toDateInputValue(minDate);
    })()}
    max={PRICING_PERIODS.reduce((latest, period) => period.end > latest ? period.end : latest, PRICING_PERIODS[0].end)}
    onChange={(e) => handleCheckOutChange(e.target.value)}
    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 pr-10 text-sm text-black [color-scheme:light] hover:border-[#1f4d3c] focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:text-base"
  />
                                        <LuCalendar className="pointer-events-none absolute right-3 top-3 h-5 w-5 text-gray-400" />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">{t("minimumStayFromCheckIn")}</p>
                                </div>

                                <div className="mb-5 sm:mb-6">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">{t("numberOfGuests")}</label>
                                    <div className="relative">
                                        <select
    value={guests}
    onChange={(e) => setGuests(Number(e.target.value))}
    className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-3 pr-10 text-sm text-black [color-scheme:light] focus:border-[#1f4f9c] focus:outline-none focus:ring-2 focus:ring-blue-200 sm:text-base"
  >
                                            <option value={1}>{t("singleOccupancyOption")}</option>
                                            <option value={2}>{t("doubleOccupancyOption")}</option>
                                        </select>
                                        <LuUsers className="pointer-events-none absolute right-3 top-3 h-5 w-5 text-gray-400" />
                                    </div>
                                </div>

                                <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:mb-6 sm:p-4">
                                    <div className="mb-2 flex items-center justify-between gap-3">
                                        <span className="text-sm font-medium text-gray-700">{t("currentSeason")}</span>
                                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${activeSeason === "Summer" ? "bg-amber-200 text-amber-800" : "bg-emerald-200 text-emerald-800"}`}>
                                            {activeSeasonLabel}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-600">
                                        {activePricingPeriod ? `G\xFCltig von ${activePricingPeriod.label}` : t("seasonPeriod")}
                                    </p>
                                </div>

                                <button
    onClick={handleSearch}
    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#1f4d3c] py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#162d2a] sm:text-base"
  >
                                    <LuSearch className="h-5 w-5" />
                                    <span>{t("updateBooking")}</span>
                                </button>
                            </div>
                        </div>

                        <div className="flex-1">
                            <div className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
                                <h2 className="text-2xl font-light text-gray-900 sm:text-3xl">{t("availableRooms")}</h2>
                                <span className={`w-fit rounded-full px-4 py-2 text-sm font-semibold ${activeSeason === "Summer" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                    {activeSeasonLabel} {t("seasonRatesSuffix")}
                                </span>
                            </div>

                            {paginatedRooms.length === 0 ? <div className="rounded-2xl border border-emerald-100 bg-white py-20 text-center shadow-lg">
                                    <LuLeaf className="mx-auto mb-4 h-16 w-16 text-emerald-200" />
                                    <p className="text-xl text-gray-500">{t("pleaseSelectValidDates")}</p>
                                    <p className="mt-2 text-gray-400">{t("minimumStayRequirement")}</p>
                                </div> : <div className="space-y-4 sm:space-y-6">
                                    {paginatedRooms.map((room) => <motion.div
    key={room.id}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className="overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-lg transition-all hover:shadow-2xl"
  >
                                            <div className="flex flex-col md:flex-row">
                                                <div className="relative h-56 bg-emerald-50 sm:h-64 md:min-h-[320px] md:w-2/5 lg:w-1/3">
                                                    <img src={room.image} alt={room.name}className="object-contain p-2" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} loading="lazy" />
                                                </div>

                                                <div className="flex-1 p-4 sm:p-5 lg:p-8">
                                                    <div className="mb-3 flex flex-wrap items-center gap-2">
                                                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">{getCategoryLabel(room)}</span>
                                                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-[#1f4d3c]">{getSeasonLabel(room.season)} {t("seasonRatesSuffix")}</span>
                                                    </div>

                                                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                        <div>
                                                            <h3 className="text-xl font-light text-gray-900 sm:text-2xl">{getRoomDisplayName(room)}</h3>
                                                            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">{getRoomDisplayDescription(room)}</p>
                                                        </div>

                                                        <div className="rounded-lg bg-emerald-50 px-4 py-2 text-right sm:min-w-36">
                                                            <div className="text-xl font-bold text-[#1f4d3c] sm:text-2xl">EUR {room.price}</div>
                                                            <div className="text-xs text-gray-600">{getGuestUsageLabel(guests)}</div>
                                                            <div className="text-xs text-gray-500">{t("perNight")}</div>
                                                        </div>
                                                    </div>

                                                    <div className="mb-4 flex flex-wrap gap-3 sm:gap-4">
                                                        <div className="flex items-center gap-2 text-sm text-gray-700">
                                                            <LuExpand className="h-4 w-4 text-[#1f4d3c]" />
                                                            <span>{room.size}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-gray-700">
                                                            <LuUsers className="h-4 w-4 text-[#1f4d3c]" />
                                                            <span>{getUpToGuestsLabel(room.occupancy)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-gray-700">
                                                            <LuStar className="h-4 w-4 text-amber-500" />
                                                            <span>{room.nights} {t("nightsLabel")}</span>
                                                        </div>
                                                    </div>

                                                    <div className="mb-4 flex flex-wrap gap-3">
                                                        {room.services.map((service) => <div
    key={`${room.id}-${service.text}`}
    title={service.text}
    className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 sm:h-10 sm:w-10"
  >
                                                                <ServiceIcon iconName={service.icon} />
                                                            </div>)}
                                                    </div>

                                                    <div className="mb-4 rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                                                        <h4 className="text-sm font-semibold text-gray-700">{t("allSeasonalRates")}</h4>
                                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 sm:col-span-2">
                                                                <p className="text-xs font-semibold text-amber-800">Gültig von {room.periodLabel}</p>
                                                                <p className="mt-1 text-sm text-gray-700">{t("singleLabel")} EUR {room.periodRates.single}</p>
                                                                <p className="text-sm text-gray-700">{t("doubleLabel")} EUR {room.periodRates.double}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mb-4 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4">
                                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                                                            <div>
                                                                <span className="block text-sm text-gray-600">{getTotalForNightsLabel(room.nights)}</span>
                                                                <span className="text-xs text-gray-500">
                                                                    {getOccupancySeasonLabel(guests === 1 ? t("singleLabel").replace(":", "") : t("doubleLabel").replace(":", ""), room.season)}
                                                                </span>
                                                            </div>
                                                            <span className="text-xl font-bold text-[#1f4d3c] sm:text-2xl">EUR {room.totalPrice.toFixed(2)}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col gap-3 sm:flex-row">
                                                        <Link
    to={RESORT_ROUTES[selectedResort]}
    className="flex-1 rounded-lg border-2 border-[#1f4d3c] px-4 py-3 text-center text-sm font-medium text-[#1f4d3c] transition-all hover:bg-emerald-50 sm:px-6 sm:text-base"
  >
                                                            {t("viewDetails")}
                                                        </Link>
                                                        <Link
    to={getBookingHref(room, checkInDate, checkOutDate, guests)}
    className="flex-1 rounded-lg bg-[#1f4d3c] px-4 py-3 text-center text-sm font-medium text-white shadow-md transition-all hover:bg-[#162d2a] sm:px-6 sm:text-base"
  >
                                                            {t("continueBooking")}
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>)}
                                </div>}

                            {totalPages > 1 && <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                                    <button
    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
    disabled={isFirstPage}
    className="rounded-lg border border-gray-300 p-2 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
  >
                                        <LuChevronLeft className="h-5 w-5" />
                                    </button>

                                    {[...Array(totalPages)].map((_, index) => <button
    key={index}
    onClick={() => setCurrentPage(index + 1)}
    className={`rounded-lg px-3 py-2 text-sm font-medium transition-all sm:px-4 sm:text-base ${safeCurrentPage === index + 1 ? "bg-[#1f4d3c] text-white shadow-md" : "border border-gray-300 text-gray-700 hover:bg-emerald-50"}`}
  >
                                            {index + 1}
                                        </button>)}

                                    <button
    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
    disabled={isLastPage}
    className="rounded-lg border border-gray-300 p-2 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
  >
                                        <LuChevronRight className="h-5 w-5" />
                                    </button>
                                </div>}
                        </div>
                    </div>
                </div>
            </div>
        </>;
};
export default Search;
